import { useMemo, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import type {
  FilterFieldSuggestion,
  FlattenedSuggestionItem,
} from './Suggestion.types.js';
import { VisuallyHidden } from '../../../core/components/visually-hidden/VisuallyHidden.js';
import { canReceiveDefaultTextFormat as _canReceiveDefaultTextFormat } from '../../../core/utils/can-receive-default-text-format.js';
import { useFilterFieldContext } from '../hooks/useFilterFieldContext.js';
import { filterFieldMessages } from '../messages.js';

/** Maximum number of default suggestions to display in aria-live announcements */
const MAX_ANNOUNCED_SUGGESTIONS = 5;

/** Debounce time for aria-live announcements */
const ARIA_LIVE_DEBOUNCE_MS = 600;

const DefaultSuggestionsAriaLiveInfo = ({
  suggestions,
}: {
  suggestions: FilterFieldSuggestion[];
}) => {
  if (!suggestions.length) {
    return null;
  }

  return (
    <ul>
      {suggestions
        .slice(0, MAX_ANNOUNCED_SUGGESTIONS)
        .map((suggestion, index) => {
          let suggestionText;

          if (_canReceiveDefaultTextFormat(suggestion.displayValue)) {
            suggestionText = suggestion.displayValue;
          } else if (_canReceiveDefaultTextFormat(suggestion.value)) {
            suggestionText = suggestion.value;
          }

          if (suggestionText) {
            return <li key={`${suggestionText}-${index}`}>{suggestionText}</li>;
          }

          return null;
        })}
    </ul>
  );
};

const LogicalOperatorSuggestionAriaLiveInfo = ({
  hasLogicalOperators,
}: {
  hasLogicalOperators: boolean;
}) => {
  if (!hasLogicalOperators) {
    return null;
  }

  return (
    <li>
      <FormattedMessage
        {...filterFieldMessages.suggestions.ariaLiveInfo.logicalOperator}
      />
    </li>
  );
};

const GeneralSuggestionsAriaLiveInfo = ({
  values,
}: {
  values: FilterFieldSuggestion[];
}) => values.map((value) => <li key={value.value}>{value.displayValue}</li>);

const RecentAndPinnedSuggestionsAriaLiveInfo = ({
  message,
  count,
}: {
  message: {
    defaultMessage: string;
    id: string;
    description: string;
  };
  count: number;
}) => {
  if (count === 0) {
    return null;
  }

  return (
    <li>
      <FormattedMessage
        {...message}
        values={{
          value: count,
        }}
      />
    </li>
  );
};

/**
 * Extracts up to `limit` FilterFieldSuggestion items from a pre-flattened list.
 */
const extractSuggestions = (
  flattenedItems: FlattenedSuggestionItem[],
  limit = MAX_ANNOUNCED_SUGGESTIONS,
): FilterFieldSuggestion[] => {
  const result: FilterFieldSuggestion[] = [];
  for (const item of flattenedItems) {
    if (result.length >= limit) {
      break;
    }
    if (item.kind === 'suggestion') {
      result.push(item.data);
    }
  }
  return result;
};

interface DebouncedAriaLiveState {
  combinedSuggestions: FilterFieldSuggestion[];
  hasAdditionalContent: boolean;
  showSearchSuggestions: boolean;
  pastedContentSuggestionValues: FilterFieldSuggestion[];
  logicalOperatorSuggestionsAvailable: boolean;
  recentSuggestionCount: number;
  pinnedSuggestionCount: number;
  searchSuggestionValues: FilterFieldSuggestion[];
}

interface AriaLiveInfoProps {
  flattenedSuggestionItems: FlattenedSuggestionItem[];
}

/**
 * Component providing hidden aria-live region to announce available suggestions to screen readers.
 * @internal
 */
export const AriaLiveInfo = ({
  flattenedSuggestionItems,
}: AriaLiveInfoProps) => {
  const {
    isValidSearch,
    pastedContentSuggestionValues,
    logicalOperatorSuggestionValues,
    recentSuggestionValues,
    pinnedSuggestionValues,
    searchSuggestionValues,
    parserConfig,
  } = useFilterFieldContext();

  const showSearchSuggestions =
    isValidSearch?.current &&
    searchSuggestionValues.length > 0 &&
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    (parserConfig?.searchConversion ?? false);

  const hasAdditionalContent =
    pastedContentSuggestionValues.length > 0 ||
    logicalOperatorSuggestionValues.length > 0 ||
    showSearchSuggestions ||
    recentSuggestionValues.length > 0 ||
    pinnedSuggestionValues.length > 0;

  // Extract only suggestion items (up to MAX_ANNOUNCED_SUGGESTIONS) from the pre-flattened list.
  const combinedSuggestions = useMemo(
    () =>
      extractSuggestions(flattenedSuggestionItems, MAX_ANNOUNCED_SUGGESTIONS),
    [flattenedSuggestionItems],
  );

  const [debouncedState, setDebouncedState] = useState<DebouncedAriaLiveState>(
    () => ({
      combinedSuggestions: [],
      hasAdditionalContent: false,
      showSearchSuggestions: false,
      pastedContentSuggestionValues: [],
      logicalOperatorSuggestionsAvailable: false,
      recentSuggestionCount: 0,
      pinnedSuggestionCount: 0,
      searchSuggestionValues: [],
    }),
  );

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedState({
        combinedSuggestions,
        hasAdditionalContent,
        showSearchSuggestions: !!showSearchSuggestions,
        pastedContentSuggestionValues,
        logicalOperatorSuggestionsAvailable:
          logicalOperatorSuggestionValues.length > 0,
        recentSuggestionCount: recentSuggestionValues.length,
        pinnedSuggestionCount: pinnedSuggestionValues.length,
        searchSuggestionValues,
      });
    }, ARIA_LIVE_DEBOUNCE_MS);

    return () => {
      clearTimeout(handle);
    };
  }, [
    combinedSuggestions,
    hasAdditionalContent,
    showSearchSuggestions,
    pastedContentSuggestionValues,
    logicalOperatorSuggestionValues.length,
    recentSuggestionValues.length,
    pinnedSuggestionValues.length,
    searchSuggestionValues,
  ]);

  const hasContent = debouncedState.combinedSuggestions.length > 0;

  return (
    <VisuallyHidden>
      <div aria-live="polite" aria-atomic="true">
        {hasContent || debouncedState.hasAdditionalContent ? (
          <>
            <FormattedMessage
              {...filterFieldMessages.suggestions.ariaLiveInfo
                .availableSuggestions}
            />
            <DefaultSuggestionsAriaLiveInfo
              suggestions={debouncedState.combinedSuggestions}
            />
            {debouncedState.hasAdditionalContent && (
              <ul>
                <GeneralSuggestionsAriaLiveInfo
                  values={debouncedState.pastedContentSuggestionValues}
                />
                <LogicalOperatorSuggestionAriaLiveInfo
                  hasLogicalOperators={
                    debouncedState.logicalOperatorSuggestionsAvailable
                  }
                />
                <RecentAndPinnedSuggestionsAriaLiveInfo
                  message={
                    filterFieldMessages.suggestions.ariaLiveInfo
                      .recentlyUsedSuggestions
                  }
                  count={debouncedState.recentSuggestionCount}
                />
                <RecentAndPinnedSuggestionsAriaLiveInfo
                  message={
                    filterFieldMessages.suggestions.ariaLiveInfo
                      .pinnedSuggestions
                  }
                  count={debouncedState.pinnedSuggestionCount}
                />
                <GeneralSuggestionsAriaLiveInfo
                  values={debouncedState.searchSuggestionValues}
                />
              </ul>
            )}
            <FormattedMessage
              {...filterFieldMessages.suggestions.ariaLiveInfo
                .moreSuggestionsAvailable}
            />
          </>
        ) : null}
      </div>
    </VisuallyHidden>
  );
};
